import { z } from 'zod';
import { inventorySchema, IInventory } from './inventory';

/**
 * Schema for validating inventory form data.
 * Extends the base inventory schema with form-specific validations.
 *
 * @property {string} code - Inventory code (max 5 characters)
 * @property {string} description - Inventory description (max 255 characters)
 */
export const inventoryFormSchema = inventorySchema;

/**
 * Default schema for inventory form initialization.
 * Extends the form schema with form-specific fields and defaults.
 * Makes nonempty string fields optional with defaults.
 *
 * @property {string} code - Optional inventory code
 * @property {string} description - Optional inventory description
 */
export const inventoryDefaultFormSchema = inventoryFormSchema.extend({
    code: z.string().optional().default(''),
    description: z.string().optional().default(''),
});

/**
 * TypeScript interface for inventory form values.
 * Generated from the inventory form schema for type safety.
 */
export type IInventoryFormValues = z.infer<typeof inventoryFormSchema>;

/**
 * Converts an Inventory model to form values for editing.
 * If no inventory is provided, returns default form values.
 *
 * @param {IInventory} [inventory] - Optional inventory model to convert
 * @returns {IInventoryFormValues} Form values ready for editing
 */
export const InventoryFormValues = (inventory ?: IInventory) : IInventoryFormValues => {
    // Handle the default case (new inventory)
    if (!inventory) {
        return inventoryDefaultFormSchema.parse({});
    }

    // Transform the IInventory data into IInventoryFormValues structure
    return inventoryFormSchema.parse({
        ...inventory,
    });
};

/**
 * Validates inventory form values against the schema.
 * Logs validation errors to console if validation fails.
 *
 * @param {IInventoryFormValues} formValues - The form values to validate
 * @returns {boolean} True if validation succeeds, false otherwise
 */
export const validateInventoryForm = (formValues : IInventoryFormValues) : boolean => {
    try {
        inventoryFormSchema.parse(formValues);
        return true;
    } catch (error) {
        if (error instanceof z.ZodError) {
            // eslint-disable-next-line no-console
            console.error('Inventory form validation failed:', error.errors);
        } else {
            // eslint-disable-next-line no-console
            console.error('An unexpected error occurred during validation:', error);
        }
        return false;
    }
};
